// Core Web Vitals and performance monitoring utilities

interface PerformanceMetric {
  name: string;
  value: number;
  timestamp: number;
  url: string;
}

class PerformanceMonitor {
  private metrics: PerformanceMetric[] = [];
  
  constructor() {
    this.setupCoreWebVitalsTracking();
    this.setupImageLoadTracking();
    this.setupNavigationTiming();
  }
  
  // Track Core Web Vitals (LCP, FID, CLS)
  private setupCoreWebVitalsTracking() {
    if (typeof window === 'undefined') return;
    
    // Largest Contentful Paint
    if ('PerformanceObserver' in window) {
      const lcpObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        const lastEntry = entries[entries.length - 1];
        
        this.recordMetric('LCP', lastEntry.startTime, window.location.href);
        
        // Log warning if LCP is poor (>4s)
        if (lastEntry.startTime > 4000) {
          console.warn(`Poor LCP detected: ${lastEntry.startTime}ms`);
        }
      });
      
      try {
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
      } catch (e) {
        console.log('LCP observer not supported');
      }
    }
    
    // First Input Delay
    if ('PerformanceObserver' in window) {
      const fidObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry: any) => {
          this.recordMetric('FID', entry.processingStart - entry.startTime, window.location.href);
          
          // Log warning if FID is poor (>300ms)
          if (entry.processingStart - entry.startTime > 300) {
            console.warn(`Poor FID detected: ${entry.processingStart - entry.startTime}ms`);
          }
        });
      });
      
      try {
        fidObserver.observe({ entryTypes: ['first-input'] });
      } catch (e) {
        console.log('FID observer not supported');
      }
    }
    
    // Cumulative Layout Shift
    if ('PerformanceObserver' in window) {
      let clsValue = 0;
      const clsObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry: any) => {
          if (!entry.hadRecentInput) {
            clsValue += entry.value;
          }
        });
        
        this.recordMetric('CLS', clsValue, window.location.href);
        
        // Log warning if CLS is poor (>0.25)
        if (clsValue > 0.25) {
          console.warn(`Poor CLS detected: ${clsValue}`);
        }
      });
      
      try {
        clsObserver.observe({ entryTypes: ['layout-shift'] });
      } catch (e) {
        console.log('CLS observer not supported');
      }
    }
  }
  
  // Track image loading performance
  private setupImageLoadTracking() {
    if (typeof window === 'undefined') return;
    
    if ('PerformanceObserver' in window) {
      const imageObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          if (entry.name.includes('.jpg') || entry.name.includes('.png') || 
              entry.name.includes('.webp') || entry.name.includes('.jpeg')) {
            
            this.recordMetric('Image Load', entry.duration, entry.name);
            
            // Log slow image loads (>2s)
            if (entry.duration > 2000) {
              console.warn(`Slow image load: ${entry.name} (${entry.duration}ms)`);
            }
          }
        });
      });
      
      try {
        imageObserver.observe({ entryTypes: ['resource'] });
      } catch (e) {
        console.log('Resource observer not supported');
      }
    }
  }
  
  // Track navigation timing
  private setupNavigationTiming() {
    if (typeof window === 'undefined') return;
    
    window.addEventListener('load', () => {
      if (window.performance && window.performance.timing) {
        const timing = window.performance.timing;
        
        const metrics = {
          'DNS Lookup': timing.domainLookupEnd - timing.domainLookupStart,
          'TCP Connection': timing.connectEnd - timing.connectStart,
          'Request Time': timing.responseStart - timing.requestStart,
          'Response Time': timing.responseEnd - timing.responseStart,
          'DOM Processing': timing.domComplete - timing.domLoading,
          'Load Complete': timing.loadEventEnd - timing.navigationStart
        };
        
        Object.entries(metrics).forEach(([name, value]) => {
          this.recordMetric(name, value, window.location.href);
        });
        
        // Log total page load time
        const totalLoad = timing.loadEventEnd - timing.navigationStart;
        console.log(`Page loaded in ${totalLoad}ms`);
        
        if (totalLoad > 3000) {
          console.warn(`Slow page load detected: ${totalLoad}ms`);
        }
      }
    });
  }
  
  // Record a performance metric
  private recordMetric(name: string, value: number, url: string) {
    const metric: PerformanceMetric = {
      name,
      value,
      timestamp: Date.now(),
      url
    };
    
    this.metrics.push(metric);
    
    // Log in development
    if (process.env.NODE_ENV === 'development') {
      console.log(`Performance: ${name} = ${value.toFixed(2)}ms`);
    }
    
    // Keep only last 100 metrics to avoid memory issues
    if (this.metrics.length > 100) {
      this.metrics = this.metrics.slice(-50);
    }
  }
  
  // Get performance summary
  getPerformanceSummary() {
    const summary: Record<string, { avg: number, count: number, latest: number }> = {};
    
    this.metrics.forEach(metric => {
      if (!summary[metric.name]) {
        summary[metric.name] = { avg: 0, count: 0, latest: 0 };
      }
      
      summary[metric.name].count++;
      summary[metric.name].avg = (summary[metric.name].avg * (summary[metric.name].count - 1) + metric.value) / summary[metric.name].count;
      summary[metric.name].latest = metric.value;
    });
    
    return summary;
  }
  
  // Check if performance is good
  isPerformanceGood(): boolean {
    const recent = this.metrics.filter(m => Date.now() - m.timestamp < 30000); // Last 30 seconds
    
    const lcp = recent.find(m => m.name === 'LCP')?.value || 0;
    const fid = recent.find(m => m.name === 'FID')?.value || 0;
    const cls = recent.find(m => m.name === 'CLS')?.value || 0;
    
    return lcp < 2500 && fid < 100 && cls < 0.1;
  }
}

// Global performance monitor instance
export const performanceMonitor = new PerformanceMonitor();

// Utility functions for image optimization
export const optimizeImageLoading = () => {
  if (typeof document === 'undefined') return;
  
  // Add loading="lazy" to images that don't have it
  const images = document.querySelectorAll('img:not([loading])');
  images.forEach((img, index) => {
    // First 3 images should be eager, rest lazy
    if (index < 3) {
      img.setAttribute('loading', 'eager');
      img.setAttribute('fetchpriority', 'high');
    } else {
      img.setAttribute('loading', 'lazy');
    }
  });
  
  // Preload critical images
  const criticalImages = document.querySelectorAll('img[data-critical="true"]');
  criticalImages.forEach(img => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = img.getAttribute('src') || '';
    document.head.appendChild(link);
  });
};

// Setup performance monitoring
export const setupPerformanceMonitoring = () => {
  if (typeof window === 'undefined') return;
  
  // Monitor performance on page load
  window.addEventListener('load', () => {
    setTimeout(() => {
      optimizeImageLoading();
      
      // Report performance summary in development
      if (process.env.NODE_ENV === 'development') {
        console.table(performanceMonitor.getPerformanceSummary());
      }
    }, 1000);
  });
  
  // Monitor performance on route changes
  let lastUrl = window.location.href;
  const observer = new MutationObserver(() => {
    if (window.location.href !== lastUrl) {
      lastUrl = window.location.href;
      setTimeout(optimizeImageLoading, 500);
    }
  });
  
  observer.observe(document.body, { childList: true, subtree: true });
};
